/**
 * Assets Manager - CMS Page for managing brand assets
 * Allows admins to upload, edit, and manage logos, images, and icons
 */
import { useState, useEffect, useCallback } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Image,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  MoreHorizontal,
  Copy,
  ExternalLink,
  ImageIcon,
  Palette,
  FileImage,
  Check,
  X,
  RefreshCw,
  LayoutGrid,
  List,
  Download,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const ASSET_TYPES = {
  logo: { label: 'Logo', icon: ImageIcon, color: 'bg-blue-500' },
  icon: { label: 'Icon', icon: Palette, color: 'bg-purple-500' },
  image: { label: 'Image', icon: FileImage, color: 'bg-green-500' }
};

const ASSET_CATEGORIES = [
  { value: 'branding', label: 'Branding' },
  { value: 'hero', label: 'Hero Images' },
  { value: 'destinations', label: 'Destinations' },
  { value: 'homepage', label: 'Homepage' },
  { value: 'about_page', label: 'About Page' },
  { value: 'services', label: 'Services' },
  { value: 'contact', label: 'Contact Page' },
  { value: 'portal', label: 'Student Portal' },
  { value: 'blog', label: 'Blog' },
  { value: 'background', label: 'Backgrounds' },
  { value: 'icons', label: 'Icons' },
  { value: 'flags', label: 'Flags' },
  { value: 'social', label: 'Social Media' },
  { value: 'general', label: 'General' }
];

// Image size recommendations for different use cases
const IMAGE_SIZE_GUIDE = {
  'Hero Images (Desktop)': '1920 x 1080 px (16:9 ratio)',
  'Hero Images (Mobile)': '800 x 600 px (4:3 ratio)',
  'Destination Cards': '600 x 400 px (3:2 ratio)',
  'Team Photos': '400 x 400 px (1:1 square)',
  'Logos': '400 x 100 px (PNG with transparency)',
  'Icons': '64 x 64 px (SVG or PNG)',
  'Blog Thumbnails': '800 x 450 px (16:9 ratio)',
  'Gallery Photos': '1200 x 800 px (3:2 ratio)'
};

export const AssetsManager = () => {
  const { token, user, logout } = useCMSAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [viewMode, setViewMode] = useState('gallery'); // 'gallery' or 'table'
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'
  const [uploadFile, setUploadFile] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    asset_key: '',
    type: 'image',
    url: '',
    category: 'general',
    description: '',
    tags: ''
  });

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  const fetchAssets = useCallback(async () => {
    try {
      let url = `${API_URL}/api/assets/?active_only=false`;
      if (filterType !== 'all') url += `&type=${filterType}`;
      if (filterCategory !== 'all') url += `&category=${filterCategory}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (handleUnauthorized(res)) return;
      
      if (res.ok) {
        const data = await res.json();
        let filtered = data.assets || [];
        
        // Client-side search filter
        if (search) {
          const searchLower = search.toLowerCase();
          filtered = filtered.filter(a => 
            a.name?.toLowerCase().includes(searchLower) ||
            a.description?.toLowerCase().includes(searchLower) ||
            a.tags?.some(t => t.toLowerCase().includes(searchLower))
          );
        }
        
        setAssets(filtered);
      }
    } catch (e) {
      toast.error('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  }, [token, search, filterType, filterCategory]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const isEdit = !!selectedAsset;
      
      // If uploading a file
      if (uploadMode === 'file' && uploadFile && !isEdit) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', uploadFile);
        formDataUpload.append('name', formData.name);
        formDataUpload.append('asset_key', formData.asset_key || formData.name.toLowerCase().replace(/\s+/g, '_'));
        formDataUpload.append('type', formData.type);
        formDataUpload.append('category', formData.category);
        formDataUpload.append('description', formData.description);
        
        const res = await fetch(`${API_URL}/api/assets/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataUpload
        });
        
        if (res.ok) {
          const data = await res.json();
          toast.success(`File uploaded! URL: ${data.url}`);
          setDialogOpen(false);
          resetForm();
          fetchAssets();
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || 'Failed to upload file');
        }
      } else {
        // URL-based asset creation/update
        const payload = {
          ...formData,
          tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
        };
        
        const url = isEdit 
          ? `${API_URL}/api/assets/${selectedAsset.asset_id}`
          : `${API_URL}/api/assets/`;
        
        const res = await fetch(url, {
          method: isEdit ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
        
        if (res.ok) {
          toast.success(isEdit ? 'Asset updated!' : 'Asset created!');
          setDialogOpen(false);
          resetForm();
          fetchAssets();
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || 'Failed to save asset');
        }
      }
    } catch (e) {
      toast.error('Failed to save asset');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAsset) return;
    setFormLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/assets/${selectedAsset.asset_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Asset deleted');
        setDeleteDialogOpen(false);
        setSelectedAsset(null);
        fetchAssets();
      } else {
        toast.error('Failed to delete asset');
      }
    } catch (e) {
      toast.error('Failed to delete asset');
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      asset_key: '',
      type: 'image',
      url: '',
      category: 'general',
      description: '',
      tags: ''
    });
    setSelectedAsset(null);
    setUploadMode('url');
    setUploadFile(null);
  };

  const openEditDialog = (asset) => {
    setSelectedAsset(asset);
    setFormData({
      name: asset.name || '',
      type: asset.type || 'image',
      url: asset.url || '',
      category: asset.category || 'general',
      description: asset.description || '',
      tags: asset.tags?.join(', ') || ''
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (asset) => {
    setSelectedAsset(asset);
    setDeleteDialogOpen(true);
  };

  const openPreview = (asset) => {
    setSelectedAsset(asset);
    setPreviewDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Alias for gallery view
  const copyUrl = copyToClipboard;
  const openEdit = openEditDialog;

  const seedDefaults = async () => {
    try {
      const res = await fetch(`${API_URL}/api/assets/seed-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchAssets();
      }
    } catch (e) {
      toast.error('Failed to seed defaults');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-display flex items-center gap-2">
              <Image className="w-7 h-7 text-blue-600" />
              Assets Manager
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Manage logos, images, and icons for your platform
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={seedDefaults}
              className="text-slate-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Seed Defaults
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Asset
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search assets..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="logo">Logos</SelectItem>
                  <SelectItem value="icon">Icons</SelectItem>
                  <SelectItem value="image">Images</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {ASSET_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* View Toggle */}
              <div className="flex border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('gallery')}
                  className={`p-2 ${viewMode === 'gallery' ? 'bg-blue-50 text-blue-600' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                  title="Gallery View"
                >
                  <LayoutGrid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 ${viewMode === 'table' ? 'bg-blue-50 text-blue-600' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                  title="Table View"
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {assets.filter(a => a.type === 'logo').length}
                </p>
                <p className="text-xs text-slate-500">Logos</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Palette className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {assets.filter(a => a.type === 'icon').length}
                </p>
                <p className="text-xs text-slate-500">Icons</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <FileImage className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {assets.filter(a => a.type === 'image').length}
                </p>
                <p className="text-xs text-slate-500">Images</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Image className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{assets.length}</p>
                <p className="text-xs text-slate-500">Total Assets</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Image Size Guide - Collapsible */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <details>
              <summary className="cursor-pointer font-semibold text-slate-800 flex items-center gap-2">
                <FileImage className="w-4 h-4 text-blue-600" />
                Recommended Image Sizes
              </summary>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(IMAGE_SIZE_GUIDE).map(([type, size]) => (
                  <div key={type} className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-slate-700">{type}</p>
                    <p className="text-xs text-slate-500 mt-1">{size}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-slate-500">
                <strong>Note:</strong> All images are responsive and adapt to different screen sizes automatically. 
                Use the recommended sizes for best quality. Images will be scaled proportionally on smaller screens.
              </p>
            </details>
          </CardContent>
        </Card>

        {/* Assets View - Gallery or Table */}
        {loading ? (
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            </CardContent>
          </Card>
        ) : assets.length === 0 ? (
          <Card>
            <CardContent className="p-0">
              <div className="text-center py-12">
                <Image className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No assets found</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => {
                    resetForm();
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Asset
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : viewMode === 'gallery' ? (
          /* Gallery View */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {assets.map((asset) => (
              <Card 
                key={asset.asset_id} 
                className="group overflow-hidden hover:shadow-lg transition-all cursor-pointer"
                onClick={() => openPreview(asset)}
              >
                <div className="relative aspect-square bg-slate-100 border-b">
                  <img 
                    src={asset.url} 
                    alt={asset.name}
                    className="w-full h-full object-contain p-2"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="%23cbd5e1" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"/%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"/%3E%3Cpath d="M21 15l-5-5L5 21"/%3E%3C/svg%3E';
                    }}
                  />
                  
                  {/* Hover overlay with actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openPreview(asset);
                      }}
                      className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                      title="Preview"
                    >
                      <Eye className="w-5 h-5 text-slate-700" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(asset);
                      }}
                      className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-5 h-5 text-slate-700" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyUrl(asset.url);
                      }}
                      className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                      title="Copy URL"
                    >
                      <Copy className="w-5 h-5 text-slate-700" />
                    </button>
                  </div>
                  
                  {/* Category badge */}
                  <div className="absolute top-2 left-2">
                    <Badge variant="secondary" className="text-xs capitalize bg-white/90">
                      {asset.category || 'general'}
                    </Badge>
                  </div>
                </div>
                
                <CardContent className="p-3">
                  <p className="font-medium text-sm text-slate-900 truncate" title={asset.name}>
                    {asset.name}
                  </p>
                  <p className="text-xs text-slate-500 truncate" title={asset.asset_id}>
                    {asset.asset_id}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Table View */
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Preview</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assets.map((asset) => {
                    const typeInfo = ASSET_TYPES[asset.type] || ASSET_TYPES.image;
                    return (
                      <TableRow key={asset.asset_id}>
                        <TableCell>
                          <div 
                            className="w-16 h-12 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                            onClick={() => openPreview(asset)}
                          >
                            <img 
                              src={asset.url} 
                              alt={asset.name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full"><svg class="w-6 h-6 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                              }}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{asset.name}</p>
                            {asset.description && (
                              <p className="text-xs text-slate-500 truncate max-w-[200px]">
                                {asset.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${typeInfo.color} text-white`}>
                            {typeInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600 capitalize">
                            {asset.category || 'General'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {asset.tags?.slice(0, 3).map((tag, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {asset.tags?.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{asset.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openPreview(asset)}>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Preview
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => copyToClipboard(asset.url)}>
                                <Copy className="w-4 h-4 mr-2" />
                                Copy URL
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditDialog(asset)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openDeleteDialog(asset)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {selectedAsset ? 'Edit Asset' : 'Add New Asset'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="name">Asset Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Primary Logo"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="type">Type *</Label>
                    <Select 
                      value={formData.type} 
                      onValueChange={(v) => setFormData({ ...formData, type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="logo">Logo</SelectItem>
                        <SelectItem value="icon">Icon</SelectItem>
                        <SelectItem value="image">Image</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSET_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="url">Asset Source *</Label>
                  
                  {/* Upload Mode Toggle - Only show for new assets */}
                  {!selectedAsset && (
                    <div className="flex gap-2 mb-2">
                      <Button
                        type="button"
                        variant={uploadMode === 'url' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setUploadMode('url')}
                      >
                        URL
                      </Button>
                      <Button
                        type="button"
                        variant={uploadMode === 'file' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setUploadMode('file')}
                      >
                        Upload File
                      </Button>
                    </div>
                  )}
                  
                  {uploadMode === 'url' || selectedAsset ? (
                    <>
                      <Input
                        id="url"
                        type="url"
                        value={formData.url}
                        onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                        placeholder="https://example.com/image.png"
                        required={uploadMode === 'url'}
                      />
                      {formData.url && (
                        <div className="mt-2 p-2 bg-slate-50 rounded-lg">
                          <img 
                            src={formData.url.startsWith('/') ? `${API_URL}${formData.url}` : formData.url} 
                            alt="Preview"
                            className="max-h-32 mx-auto object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        id="file"
                        type="file"
                        accept="image/*,.pdf,.svg"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadFile(file);
                            // Auto-generate asset_key from filename
                            const key = file.name.replace(/\.[^/.]+$/, '').toLowerCase().replace(/\s+/g, '_');
                            setFormData({ ...formData, asset_key: key });
                          }
                        }}
                        required={uploadMode === 'file'}
                      />
                      {uploadFile && (
                        <div className="mt-2 p-2 bg-slate-50 rounded-lg text-center">
                          <p className="text-sm text-slate-600">
                            Selected: <strong>{uploadFile.name}</strong> ({Math.round(uploadFile.size / 1024)} KB)
                          </p>
                          {uploadFile.type.startsWith('image/') && (
                            <img 
                              src={URL.createObjectURL(uploadFile)} 
                              alt="Preview"
                              className="max-h-32 mx-auto object-contain mt-2"
                            />
                          )}
                        </div>
                      )}
                      <div>
                        <Label htmlFor="asset_key">Asset Key</Label>
                        <Input
                          id="asset_key"
                          value={formData.asset_key}
                          onChange={(e) => setFormData({ ...formData, asset_key: e.target.value })}
                          placeholder="e.g., team_harvinder"
                        />
                        <p className="text-xs text-slate-500 mt-1">Unique identifier for the asset</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this asset..."
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label htmlFor="tags">Tags (comma separated)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="logo, branding, primary"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading}>
                  {formLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : selectedAsset ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  {selectedAsset ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Asset</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-slate-600">
                Are you sure you want to delete <strong>{selectedAsset?.name}</strong>? 
                This action cannot be undone.
              </p>
              {selectedAsset?.url && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <img 
                    src={selectedAsset.url} 
                    alt={selectedAsset.name}
                    className="max-h-20 mx-auto object-contain"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={formLoading}
              >
                {formLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>{selectedAsset?.name}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-slate-100 rounded-xl p-8 flex items-center justify-center min-h-[200px]">
                <img 
                  src={selectedAsset?.url} 
                  alt={selectedAsset?.name}
                  className="max-w-full max-h-[300px] object-contain"
                />
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-500 truncate flex-1 mr-2">
                    {selectedAsset?.url}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(selectedAsset?.url)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                {selectedAsset?.description && (
                  <p className="text-sm text-slate-600">{selectedAsset.description}</p>
                )}
                <div className="flex items-center gap-2">
                  <Badge className={`${ASSET_TYPES[selectedAsset?.type]?.color || 'bg-slate-500'} text-white`}>
                    {ASSET_TYPES[selectedAsset?.type]?.label || 'Asset'}
                  </Badge>
                  <span className="text-sm text-slate-500 capitalize">
                    {selectedAsset?.category}
                  </span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => window.open(selectedAsset?.url, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in New Tab
              </Button>
              <Button onClick={() => setPreviewDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AssetsManager;
